import { useNextStep } from 'nextstepjs';
import { matchPath } from 'react-router-dom';
import { memo, useId, useState, useEffect } from 'react';

import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import ButtonBase from '@mui/material/ButtonBase';
import ListSubheader from '@mui/material/ListSubheader';
import useMediaQuery from '@mui/material/useMediaQuery';

import { usePathname } from 'src/routes/hooks';

import Iconify from 'src/components/iconify';

import NavList from './nav-list';
import { NavProps, NavGroupProps, NavItemBaseProps } from '../types';

// ----------------------------------------------------------------------

function NavSectionVertical({ data, slotProps, ...other }: NavProps) {
  return (
    <Stack component="nav" id="nav-section-vertical" {...other}>
      {data.map((group, index) => (
        <Group
          key={group.subheader || index}
          subheader={group.subheader}
          collapsible={group.collapsible}
          items={group.items}
          slotProps={slotProps}
        />
      ))}
    </Stack>
  );
}

export default memo(NavSectionVertical);

// ----------------------------------------------------------------------

function Group({ subheader, items, collapsible, slotProps }: NavGroupProps) {
  const pathname = usePathname();
  const { currentTour } = useNextStep();
  const panelId = useId();
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const containsActive = (entries: NavItemBaseProps[]): boolean =>
    entries.some(
      (item) =>
        !!matchPath({ path: item.path, end: item.path === '/dashboard' }, pathname) ||
        (item.children && containsActive(item.children))
    );
  const active = containsActive(items);
  const [expanded, setExpanded] = useState(active);

  useEffect(() => {
    setExpanded(active);
  }, [active, pathname]);

  // The existing onboarding tour targets menu IDs across several groups.
  const open = expanded || !!currentTour;
  const renderContent = items.map((list) => (
    <NavList key={list.title} data={list} depth={1} slotProps={slotProps} />
  ));

  if (collapsible && subheader) {
    return (
      <Stack sx={{ px: 2 }}>
        <ButtonBase
          onClick={() => setExpanded((previous) => !previous)}
          aria-expanded={open}
          aria-controls={panelId}
          sx={{
            justifyContent: 'space-between',
            minHeight: 44,
            px: 1.5,
            mb: 0.5,
            borderRadius: 1,
            typography: 'overline',
            color: active ? 'primary.main' : 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          {subheader}
          <Iconify
            width={16}
            icon={open ? 'eva:arrow-ios-downward-fill' : 'eva:arrow-ios-forward-fill'}
          />
        </ButtonBase>
        <Collapse id={panelId} in={open} timeout={reduceMotion ? 0 : 'auto'}>
          <Stack>{renderContent}</Stack>
        </Collapse>
      </Stack>
    );
  }

  return (
    <Stack sx={{ px: 2 }}>
      {subheader ? (
        <>
          <ListSubheader
            disableGutters
            disableSticky
            sx={{
              fontSize: 11,
              typography: 'overline',
              display: 'inline-flex',
              color: 'text.disabled',
              mb: `${slotProps?.gap || 4}px`,
              p: (theme) => theme.spacing(2, 1, 1, 1.5),
              transition: (theme) =>
                theme.transitions.create(['color'], {
                  duration: theme.transitions.duration.shortest,
                }),
              ...slotProps?.subheader,
            }}
          >
            {subheader}
          </ListSubheader>

          <Stack>{renderContent}</Stack>
        </>
      ) : (
        renderContent
      )}
    </Stack>
  );
}
